import React from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Phone, Mail, MapPin } from 'lucide-react';

export const Footer = () => {
  const { t } = useTranslation();
  const currentYear = new Date().getFullYear();

  return (
    <footer className="bg-[hsl(222,47%,11%)] text-white" data-testid="main-footer">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12">
          {/* Company Info */}
          <div className="space-y-4">
            <img 
              src="https://customer-assets.emergentagent.com/job_remodel-pros-1/artifacts/66uykm11_logo%20edx%20para%20site.png" 
              alt="EDX Construction" 
              className="h-16 w-auto mb-4"
            />
            <p className="text-gray-300 text-sm leading-relaxed">
              {t('footer.tagline')}
            </p>
          </div>

          {/* Quick Links */}
          <div>
            <h3 className="text-lg font-bold mb-4" style={{ fontFamily: 'Outfit' }}>
              {t('nav.home')}
            </h3>
            <ul className="space-y-2">
              <li>
                <Link to="/pavers" className="text-gray-300 hover:text-white text-sm transition-colors duration-200">
                  {t('nav.pavers')}
                </Link>
              </li>
              <li>
                <Link to="/remodeling" className="text-gray-300 hover:text-white text-sm transition-colors duration-200">
                  {t('nav.remodeling')}
                </Link>
              </li>
              <li>
                <Link to="/projects" className="text-gray-300 hover:text-white text-sm transition-colors duration-200">
                  {t('nav.projects')}
                </Link>
              </li>
              <li>
                <Link to="/about" className="text-gray-300 hover:text-white text-sm transition-colors duration-200">
                  {t('nav.about')}
                </Link>
              </li>
            </ul>
          </div>

          {/* Services */}
          <div>
            <h3 className="text-lg font-bold mb-4" style={{ fontFamily: 'Outfit' }}>
              {t('services.title')}
            </h3>
            <ul className="space-y-2">
              <li>
                <Link to="/pavers" className="text-gray-300 hover:text-white text-sm transition-colors duration-200">
                  {t('services.pavers.title')}
                </Link>
              </li>
              <li>
                <Link to="/remodeling" className="text-gray-300 hover:text-white text-sm transition-colors duration-200">
                  {t('services.kitchen.title')}
                </Link>
              </li>
              <li>
                <Link to="/remodeling" className="text-gray-300 hover:text-white text-sm transition-colors duration-200">
                  {t('services.bathroom.title')}
                </Link>
              </li>
              <li>
                <Link to="/remodeling" className="text-gray-300 hover:text-white text-sm transition-colors duration-200">
                  {t('services.interior.title')}
                </Link>
              </li>
            </ul>
          </div>

          {/* Contact Info */}
          <div>
            <h3 className="text-lg font-bold mb-4" style={{ fontFamily: 'Outfit' }}>
              {t('nav.contact')}
            </h3>
            <ul className="space-y-3">
              <li>
                <a
                  href="tel:+14074510902"
                  className="flex items-center text-gray-300 hover:text-white text-sm transition-colors duration-200"
                  data-testid="footer-phone-1"
                >
                  <Phone className="w-4 h-4 mr-2" />
                  +1 (407) 451-0902
                </a>
              </li>
              <li>
                <a
                  href="tel:+14074961867"
                  className="flex items-center text-gray-300 hover:text-white text-sm transition-colors duration-200"
                  data-testid="footer-phone-2"
                >
                  <Phone className="w-4 h-4 mr-2" />
                  +1 (407) 496-1867
                </a>
              </li>
              <li>
                <a
                  href="mailto:edxconstruction1@gmail.com"
                  className="flex items-center text-gray-300 hover:text-white text-sm transition-colors duration-200"
                  data-testid="footer-email"
                >
                  <Mail className="w-4 h-4 mr-2" />
                  edxconstruction1@gmail.com
                </a>
              </li>
              <li>
                <a
                  href="https://www.instagram.com/edx.construction"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center text-gray-300 hover:text-white text-sm transition-colors duration-200"
                  data-testid="footer-instagram"
                >
                  <svg className="w-4 h-4 mr-2" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/>
                  </svg>
                  @edx.construction
                </a>
              </li>
              <li className="flex items-start text-gray-300 text-sm">
                <MapPin className="w-4 h-4 mr-2 mt-0.5 flex-shrink-0" />
                <span>{t('serviceAreas.subtitle')}</span>
              </li>
            </ul>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="mt-12 pt-8 border-t border-gray-700">
          <div className="flex flex-col md:flex-row justify-between items-center space-y-4 md:space-y-0">
            <p className="text-gray-400 text-sm">
              © {currentYear} EDX Construction. {t('footer.rights')}
            </p>
            <div className="flex items-center space-x-6">
              <Link to="/privacy" className="text-gray-400 hover:text-white text-sm transition-colors duration-200">
                {t('footer.privacy')}
              </Link>
              <Link to="/terms" className="text-gray-400 hover:text-white text-sm transition-colors duration-200">
                {t('footer.terms')}
              </Link>
              <span className="text-gray-600">|</span>
              <Link 
                to="/edx-admin" 
                className="text-gray-500 hover:text-gray-300 text-xs transition-colors duration-200 opacity-50 hover:opacity-100"
                data-testid="footer-admin-access"
              >
                {t('footer.adminAccess')}
              </Link>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};